// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import type { RouterHistory } from 'react-router';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Table from 'shared/components/common/Table';
import Icon from 'shared/components/common/Icon';
import Page from 'main-app/components/Page';
import moment from 'moment';

type Props = {
  history: RouterHistory,
  match: {
    params: {
      id?: string,
    },
  },
};

type State = {
  expandedRows: Set<string>,
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 16px;
`;

const ExpandedSection = styled.div`
  padding: 16px;
  background-color: #f8f9fa;
  border-top: 1px solid #e0e0e0;
`;

const LineItemsTable = styled.div`
  margin-top: 8px;
`;

const LineItemRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
  font-size: 14px;

  &:first-child {
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    font-size: 12px;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const POLink = styled.span`
  color: #3b82f6;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #2563eb;
  }
`;

class ReceiptHistory extends PureComponent<Props, State> {
  state = {
    expandedRows: new Set(),
  };

  toggleRowExpansion = (receiptId: string) => {
    const { expandedRows } = this.state;
    const newExpandedRows = new Set(expandedRows);
    
    if (newExpandedRows.has(receiptId)) {
      newExpandedRows.delete(receiptId);
    } else {
      newExpandedRows.add(receiptId);
    }
    
    this.setState({ expandedRows: newExpandedRows });
  };

  handlePOClick = (poId: string) => {
    this.props.history.push(`/purchase-order/${poId}`);
  };

  formatDate = (date: ?string) => {
    if (!date) return '-';
    return moment(date).format('MMM DD, YYYY HH:mm');
  };

  render() {
    const { history, match } = this.props;
    const { expandedRows } = this.state;
    const purchaseOrderId = match.params.id || null;

    return (
      <Page title={i18n.t('Receipt History')} history={history}>
        <QueryRenderer
          environment={relayEnvironment}
          dataFrom="STORE_THEN_NETWORK"
          query={graphql`
            query ReceiptHistoryQuery($first: Int, $purchaseOrderId: ID) {
              poReceiptHistory(first: $first, purchaseOrderId: $purchaseOrderId)
                @connection(key: "ReceiptHistory_poReceiptHistory") {
                edges {
                  node {
                    id
                    purchaseOrder {
                      id
                      poNumber
                    }
                    receivedBy {
                      id
                      firstName
                      lastName
                    }
                    receivedAt
                    notes
                    receiptLines {
                      id
                      item {
                        id
                        itemNumber
                        name
                      }
                      quantityReceived
                      lotNumber
                    }
                  }
                }
                totalCount
              }
            }
          `}
          variables={{ first: 1000, purchaseOrderId }}
          render={({ error, props }) => {
            if (error) {
              return <div>Error: {error.message}</div>;
            }

            if (!props) {
              return <Loader />;
            }

            const edges = props.poReceiptHistory?.edges || [];
            const totalCount = props.poReceiptHistory?.totalCount || 0;
            const receipts = edges
              .filter(edge => edge && edge.node)
              .map(edge => edge.node);

            if (receipts.length === 0) {
              return (
                <Wrapper>
                  <Header>
                    <Title>{i18n.t('Receipt History')}</Title>
                  </Header>
                  <EmptyState>
                    <Icon type="circle-inventory" size={48} />
                    <div style={{ marginTop: '16px' }}>
                      {i18n.t('No receipt records found')}
                    </div>
                  </EmptyState>
                </Wrapper>
              );
            }

            const columns = [
              {
                id: 'receivedAt',
                Header: i18n.t('Receipt Date'),
                accessor: (row) => this.formatDate(row.receivedAt),
                width: 180,
              },
              {
                id: 'poNumber',
                Header: i18n.t('PO Number'),
                accessor: () => null,
                width: 150,
                Cell: ({ row }) => (
                  <POLink onClick={(e) => {
                    e.stopPropagation();
                    this.handlePOClick(row.purchaseOrder?.id);
                  }}>
                    {row.purchaseOrder?.poNumber || '-'}
                  </POLink>
                ),
              },
              {
                id: 'receivedBy',
                Header: i18n.t('Received By'),
                accessor: (row) => {
                  const user = row.receivedBy;
                  return user ? `${user.firstName} ${user.lastName}` : '-';
                },
                width: 180,
              },
              {
                id: 'itemsCount',
                Header: i18n.t('Items Received'),
                accessor: (row) => row.receiptLines?.length || 0,
                width: 150,
              },
              {
                id: 'notes',
                Header: i18n.t('Notes'),
                accessor: (row) => row.notes || '-',
                width: 250,
              },
            ];

            const tableData = receipts.map(receipt => ({
              id: receipt.id,
              receivedAt: receipt.receivedAt,
              purchaseOrder: receipt.purchaseOrder,
              receivedBy: receipt.receivedBy,
              receiptLines: receipt.receiptLines,
              notes: receipt.notes,
            }));

            return (
              <Wrapper>
                <Header>
                  <Title>
                    {i18n.t('Receipt History')}
                    {purchaseOrderId && i18n.t(' - PO Specific')}
                  </Title>
                </Header>

                <Table
                  columns={columns}
                  data={tableData}
                  onRowClick={(row) => this.toggleRowExpansion(row.id)}
                  getRowProps={() => ({
                    style: {
                      cursor: 'pointer',
                    },
                  })}
                  renderExpandedRow={(row) => {
                    if (!expandedRows.has(row.id)) return null;

                    return (
                      <ExpandedSection>
                        <div style={{ fontWeight: 600, marginBottom: '12px' }}>
                          {i18n.t('Receipt Line Items')}
                        </div>
                        <LineItemsTable>
                          <LineItemRow>
                            <div>{i18n.t('Item Number')}</div>
                            <div>{i18n.t('Item Name')}</div>
                            <div>{i18n.t('Quantity Received')}</div>
                            <div>{i18n.t('Lot Number')}</div>
                          </LineItemRow>
                          {row.receiptLines?.map(line => (
                            <LineItemRow key={line.id}>
                              <div>{line.item?.itemNumber || '-'}</div>
                              <div>{line.item?.name || '-'}</div>
                              <div>{line.quantityReceived}</div>
                              <div>{line.lotNumber || '-'}</div>
                            </LineItemRow>
                          ))}
                        </LineItemsTable>
                      </ExpandedSection>
                    );
                  }}
                />
              </Wrapper>
            );
          }}
        />
      </Page>
    );
  }
}

export default ReceiptHistory;
